/* useOperationalVisibility.js — Role-sensitive operational visibility.
 *
 * Tiny client-side hook that hydrates the lightweight visibility
 * preferences (4-5 toggles) from localStorage and exposes:
 *
 *   • audience      — internal | client | third_party  (derived from user.role)
 *   • settings      — current toggle state
 *   • setSettings   — patch a partial settings object (persists immediately)
 *   • filterLines   — (rawLines) => { lines, showTime } already
 *                     audience-filtered + settings-applied. Safe to
 *                     feed straight to <OperationalVisibilityList>.
 *
 * Doctrine: /app/memory/OPERATIONAL_VOCABULARY_DOCTRINE.md
 *   • Operational visibility, NOT personal presence.
 *   • Settings are calm enterprise toggles — never permission matrices.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../data/authStore";
import {
  AUDIENCE_INTERNAL,
  AUDIENCE_THIRD_PARTY,
  VISIBILITY_DEFAULTS,
  VISIBILITY_SETTINGS_KEY,
  applyVisibilitySettings,
  audienceForRole,
  visibilityLinesForAudience,
} from "../constants/operationalMomentum";

function readSettings() {
  if (typeof window === "undefined") return { ...VISIBILITY_DEFAULTS };
  try {
    const raw = window.localStorage.getItem(VISIBILITY_SETTINGS_KEY);
    if (!raw) return { ...VISIBILITY_DEFAULTS };
    const parsed = JSON.parse(raw);
    return { ...VISIBILITY_DEFAULTS, ...(parsed || {}) };
  } catch {
    return { ...VISIBILITY_DEFAULTS };
  }
}

function persistSettings(next) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(VISIBILITY_SETTINGS_KEY, JSON.stringify(next));
  } catch { /* calm degradation — quota or private mode */ }
}

export function useOperationalVisibility(overrideAudience) {
  const { user } = useAuth();
  const [settings, setSettingsState] = useState(() => readSettings());

  // Cross-tab sync — if another tab updates the prefs, mirror them.
  useEffect(() => {
    function onStorage(e) {
      if (e.key === VISIBILITY_SETTINGS_KEY) setSettingsState(readSettings());
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const audience = useMemo(() => {
    if (overrideAudience) return overrideAudience;
    const role = user?.role;
    const derived = audienceForRole(role);
    // Honour the "internal-only" toggle by elevating only when the
    // viewer is actually internal — never lifts a client/third-party.
    if (settings.internal_only && derived !== AUDIENCE_INTERNAL) {
      return AUDIENCE_THIRD_PARTY;
    }
    return derived;
  }, [user?.role, overrideAudience, settings.internal_only]);

  const setSettings = useCallback((patch) => {
    setSettingsState((prev) => {
      const next = { ...prev, ...(patch || {}) };
      persistSettings(next);
      return next;
    });
  }, []);

  const filterLines = useCallback((rawLines) => {
    const audienced = visibilityLinesForAudience(rawLines, audience);
    return applyVisibilitySettings(audienced, settings);
  }, [audience, settings]);

  return { audience, settings, setSettings, filterLines };
}
